import type { PropsWithChildren } from 'react';
import { createContext, useContext, useRef } from 'react';

type EventCallback = (data?: any) => void;

interface EventContextProps {
  subscribe: (eventType: string, callback: EventCallback) => () => void;
  publish: (eventType: string, data?: any) => void;
}

const EventContext = createContext<EventContextProps | undefined>(undefined);

export const useEventContext = (): EventContextProps => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEventContext must be used within an EventProvider');
  }
  return context;
};

export const EventProvider = ({ children }: PropsWithChildren) => {
  const eventCallbacksRef = useRef<{ [key: string]: EventCallback[] }>({});

  const subscribe = (
    eventType: string,
    callback: EventCallback
  ): (() => void) => {
    if (!eventCallbacksRef.current[eventType]) {
      eventCallbacksRef.current[eventType] = [];
    }
    eventCallbacksRef.current[eventType].push(callback);
    return () => {
      eventCallbacksRef.current[eventType] = eventCallbacksRef.current[
        eventType
      ].filter((cb) => cb !== callback);
    };
  };

  const publish = (eventType: string, data?: any): void => {
    if (eventCallbacksRef.current[eventType]) {
      eventCallbacksRef.current[eventType].forEach((callback) =>
        callback(data)
      );
    }
  };

  return (
    <EventContext.Provider value={{ subscribe, publish }}>
      {children}
    </EventContext.Provider>
  );
};
